import { Skeleton, Text, Tooltip } from '@radix-ui/themes';
import { Bot, ChevronDown, ChevronUp, Mail, MailOpen, Mouse, Send, Smartphone } from 'lucide-react';
import { Accordion } from 'radix-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '../../i18n';
import { getScore } from '../common';
import type { Activity, Application } from './Header';

const getActivityMessage = (activityType: string, activitySubType: string) => {
  if (activityType === 'sms') {
    if (activitySubType === 'delivered') {
      return i18n.t('swiper.activityLog.smsSent');
    }
  }
};

const ActivityLog = ({
  application,
  selectedCallback,
  preApplications,
  selected,
}: {
  application?: Application;
  selectedCallback: (id: string) => void;
  preApplications?: (
    | {
        id: string;
        job: { title: string; threshold: number };
        stage: number;
        score: number;
        status: string;
        candidate: {
          firstName: string;
          lastName: string;
        };
      }
    | undefined
  )[];
  selected: number;
}) => {
  const { t } = useTranslation();
  const [accordionSelected, setAccordionSelected] = useState<string>();
  const communicationLog: Activity[] = [];
  const actionLog: Activity[] = [];
  if (application && application.activityLog) {
    application.activityLog.forEach((activity) => {
      if (activity) {
        if (!activity.createdAt || (activity.actionType === 'sms' && activity.actionSubType === 'scheduled'))
          return null;
        if (
          activity.actionType === 'email_update' ||
          (activity.actionType === 'sms' && activity.actionSubType === 'delivered')
        )
          communicationLog.push(activity);
        else actionLog.push(activity);
      }
      return null;
    });
  }

  if (application)
    return (
      <div className="activity-log">
        {actionLog && (
          <Accordion.Root
            type="single"
            defaultValue={actionLog.length > 0 ? 'activity' : 'email'}
            collapsible
            onValueChange={setAccordionSelected}
          >
            <Accordion.Item value="activity" data-ph-capture-attribute-region="activity_log">
              <Accordion.Header>
                <Accordion.Trigger className="font-bold w-full">
                  <span className="clickable flex justify-between">
                    <span>{t('swiper.activityLog.activityHeader')}</span>
                    {accordionSelected === 'activity' ? <ChevronUp /> : <ChevronDown />}
                  </span>
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className="accordion-content my-1 flex flex-col gap-2">
                {actionLog.map((activity, idx) => {
                  if (!activity.createdAt) return null;

                  const createdAt = new Date(activity.createdAt);
                  return (
                    <div key={`activity-${activity.createdAt}-${idx}`} className="flex! justify-between items-center">
                      <div className="flex! items-center">
                        <span>{getActivityIcon(activity)}</span>
                        <span className="capitalize">
                          {activity.message ?? getActivityMessage(activity.actionType, activity.actionSubType)}
                        </span>
                      </div>
                      <Tooltip content={<Text size="3">{createdAt.toLocaleString('sv-se')}</Text>}>
                        <span>{createdAt.toLocaleDateString('sv-se')}</span>
                      </Tooltip>
                    </div>
                  );
                })}
                {application.activityLog.length === 0 && <span>{t('swiper.activityLog.empty')}</span>}
              </Accordion.Content>
            </Accordion.Item>
            <Accordion.Item value="email" data-ph-capture-attribute-region="activity_log">
              <Accordion.Header>
                <Accordion.Trigger className="font-bold w-full">
                  <span className="clickable flex justify-between">
                    <span>{t('swiper.activityLog.communicationsHeader')}</span>
                    {accordionSelected === 'email' ? <ChevronUp /> : <ChevronDown />}
                  </span>
                </Accordion.Trigger>
              </Accordion.Header>
              <Accordion.Content className="accordion-content">
                {communicationLog.map((activity, idx) => {
                  if (!activity.createdAt) return null;
                  const createdAt = new Date(activity.createdAt);
                  return (
                    <div key={`email-${activity.createdAt}-${idx}`} className="flex justify-between items-center">
                      <div className="flex items-center">
                        <span>{getActivityIcon(activity)}</span>
                        <span className="capitalize">
                          {activity.message ?? getActivityMessage(activity.actionType, activity.actionSubType)}
                        </span>
                      </div>
                      <Tooltip content={<Text size="3">{createdAt.toLocaleString('sv-se')}</Text>}>
                        <span>{createdAt.toLocaleDateString('sv-se')}</span>
                      </Tooltip>
                    </div>
                  );
                })}
                {application.activityLog.length === 0 && <span>{t('swiper.activityLog.empty')}</span>}
              </Accordion.Content>
            </Accordion.Item>
          </Accordion.Root>
        )}
        <div className="previus-applications my-4">
          {preApplications && (
            <>
              <h2>{t('swiper.activityLog.previousApplications')}</h2>
              <div className="user-select-none">
                {preApplications.map((application, idx) => {
                  if (application)
                    return (
                      <Tooltip
                        content={<Text size="3">{application.job.title}</Text>}
                        key={`${application.id}-${idx}-tooltip`}
                      >
                        <div
                          key={`${application.id}-${idx}-container`}
                          className={
                            idx === selected
                              ? 'selected flex w-full justify-between'
                              : 'flex w-full justify-between align-middle'
                          }
                          onClick={() => {
                            selectedCallback(application.id);
                          }}
                          onDoubleClick={() => {
                            navigator.clipboard.writeText(`{_id:ObjectId('${application.id}')}`);
                          }}
                        >
                          <span className="my-auto ml-2 truncate">{application.job.title}</span>
                          <span className="m-1 mx-2">
                            {getScore({ application, threshold: application.job.threshold, size: 'md' })}
                          </span>
                        </div>
                      </Tooltip>
                    );
                  else return null;
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  else return <Skeleton />;
};

export default ActivityLog;

const getActivityIcon = (activity: Activity) => {
  let icon;

  if (activity.actionType === 'email_update') {
    switch (activity.status) {
      case 'sent':
        icon = <Send size={16} />;
        break;
      case 'opened':
        icon = <MailOpen size={16} />;
        break;
      case 'unique_opened':
        icon = <MailOpen size={16} />;
        break;

      case 'delivered':
        icon = <Mail size={16} />;
        break;

      case 'clicked':
        icon = <Mouse size={16} />;
        break;

      default:
        icon = <Mail size={16} />;
        break;
    }
    return (
      <span
        className="score-pill-md m-1 mx-2 purple inline-flex! items-center justify-center"
        style={{ fontSize: '1.4rem' }}
      >
        {icon}
      </span>
    );
  } else if (activity.createdBy)
    return (
      <span className="score-pill-md m-1 mx-2 purple capitalize inline-flex! items-center justify-center">
        {activity.createdBy.slice(0, 1)}
      </span>
    );
  else if (activity.actionType === 'sms') {
    switch (activity.actionSubType) {
      case 'delivered':
        icon = <Smartphone size={16} />;
        break;
      default:
        icon = <Smartphone size={16} />;
        break;
    }

    return (
      <span
        className="score-pill-md m-1 mx-2 purple inline-flex! items-center justify-center"
        style={{ fontSize: '1.4rem' }}
      >
        {icon}
      </span>
    );
  } else
    return (
      <span
        className="score-pill-md m-1 mx-2 purple inline-flex! items-center justify-center"
        style={{ fontSize: '1.8rem' }}
      >
        <Bot size={16} />
      </span>
    );
};
